import { useState } from "react";
import { Building2, ChevronDown, Crown, ArrowLeftRight, Check, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useTenant } from "@/contexts/TenantContext";
import { useAuth } from "@/contexts/AuthContext";

/**
 * TenantSwitcher — visible to super-super-admin only.
 * Allows switching active tenant via impersonation. The current tenant is
 * always displayed (subdomain + name + role-tag if impersonated).
 */
export default function TenantSwitcher() {
  const { user } = useAuth();
  const { tenant, tenants, isSuperSuperAdmin, impersonating, switchTenant, stopImpersonation } = useTenant();
  const [busy, setBusy] = useState(false);

  if (!tenant) return null;

  // For non-super-super-admin users, render as a static tenant badge (informational)
  if (!isSuperSuperAdmin) {
    return (
      <div
        data-testid="tenant-badge"
        className="hidden md:flex h-9 items-center gap-2 rounded-full border border-border bg-card px-3 text-xs"
        title={`Tenant: ${tenant.name}`}
      >
        <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-overline text-muted-foreground">Tenant</span>
        <span className="font-display font-bold text-sm truncate max-w-[140px]">{tenant.name}</span>
      </div>
    );
  }

  async function pick(tid) {
    if (busy || tid === tenant?.id) return;
    setBusy(true);
    try {
      await switchTenant(tid);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="hidden md:flex items-center gap-1.5">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            data-testid="tenant-switcher-trigger"
            variant="outline"
            className="h-9 rounded-full px-3 gap-2 border-secondary/40 bg-secondary/5 hover:bg-secondary/10"
          >
            {impersonating ? (
              <ArrowLeftRight className="w-3.5 h-3.5 text-secondary" />
            ) : (
              <Crown className="w-3.5 h-3.5 text-secondary" />
            )}
            <span className="text-overline text-muted-foreground hidden lg:inline">
              {impersonating ? "Viewing" : "Tenant"}
            </span>
            <span className="font-display font-bold text-sm truncate max-w-[160px]">{tenant.name}</span>
            <ChevronDown className="w-3.5 h-3.5 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72">
          <DropdownMenuLabel className="flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5 text-secondary" />
            Super Super Admin · {user?.email}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-overline text-muted-foreground">Switch Tenant</DropdownMenuLabel>
          {tenants.map((t) => (
            <DropdownMenuItem
              key={t.id}
              data-testid={`tenant-option-${t.subdomain}`}
              onClick={() => pick(t.id)}
              disabled={busy}
              className="flex items-center gap-2"
            >
              <Building2 className="w-3.5 h-3.5" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{t.name}</div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {t.subdomain} · {t.is_operator ? "Operator" : "Customer"}
                </div>
              </div>
              {t.id === tenant.id && <Check className="w-4 h-4 text-secondary" />}
            </DropdownMenuItem>
          ))}
          {impersonating && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="stop-impersonation"
                onClick={stopImpersonation}
                className="text-destructive focus:text-destructive"
              >
                <ArrowLeftRight className="w-3.5 h-3.5 mr-2" /> Stop impersonation & return
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {impersonating && (
        <div
          data-testid="impersonating-pill"
          className="h-9 flex items-center gap-1.5 rounded-full bg-amber-100 dark:bg-amber-500/20 border border-amber-300 dark:border-amber-500/40 px-2.5 text-[11px] font-medium text-amber-800 dark:text-amber-200"
          title="You are impersonating this tenant"
        >
          <ArrowLeftRight className="w-3 h-3" />
          Impersonating
        </div>
      )}
    </div>
  );
}
